import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { ClipboardList, Bike } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import api from '../../lib/api';
import { StatusBadge } from '../../components/commerce/StatusBadge';
import { EmptyState } from '../../components/commerce/EmptyState';
import { fmtRM2, timeAgo } from '../../data/mockData';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';

const ASSIGNABLE = ['READY_FOR_PICKUP', 'PREPARING', 'ACCEPTED'];

export function OrdersAdmin() {
  const { orders, deliveries, adminAssignRiderToOrder } = useApp();
  const [assignTarget, setAssignTarget] = useState(null);
  const [riders, setRiders] = useState([]);
  useEffect(() => {
    api.get('/admin/users')
      .then((r) => setRiders(r.data.filter((u) => u.role === 'rider').map((u) => ({ id: u.riderId || u.id, name: u.name }))))
      .catch(() => {});
  }, []);

  const deliveryFor = (orderId) => deliveries.find((d) => d.orderId === orderId);

  const assign = async (rider) => {
    const res = await adminAssignRiderToOrder(assignTarget.id, rider.id);
    if (res?.ok) toast.success(`${rider.name} assigned to #${assignTarget.orderNumber}`);
    setAssignTarget(null);
  };

  return (
    <div data-testid="admin-orders" className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-kelate-ink">All Orders</h2>
        <span className="text-xs text-kelate-soft">Assign a rider once the merchant marks an order ready.</span>
      </div>
      {orders.length === 0 && <EmptyState icon={ClipboardList} title="No orders yet" description="Customer orders will appear here." />}
      {orders.map((o) => {
        const d = deliveryFor(o.id);
        const assignable = ASSIGNABLE.includes(o.orderStatus);
        return (
          <div key={o.id} data-testid={`admin-order-${o.orderNumber}`} className="rounded-3xl bg-kelate-surface p-4 shadow-[0_1px_4px_rgba(81,68,55,0.08)]">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <span className="text-sm font-semibold text-kelate-ink">Order #{o.orderNumber}</span>
                <span className="ml-2 text-xs text-kelate-soft">{timeAgo(o.createdAt)}</span>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={o.orderStatus} />
                <StatusBadge status={o.paymentStatus} />
              </div>
            </div>
            <div className="mt-2 grid gap-1 text-xs text-kelate-soft sm:grid-cols-2">
              <span>Store: <b className="text-kelate-ink">{o.merchantName}</b></span>
              <span>Customer: {o.customerName} · {o.customerPhone}</span>
              <span>Items: {o.items.map((i) => `${i.name} x${i.qty}`).join(', ')}</span>
              <span>Total: <b className="text-kelate-darkwood">{fmtRM2(o.totalCustomerDue)}</b> · {o.paymentMethod}</span>
              <span>Deliver to: {o.address?.line1}, {o.address?.district}</span>
              <span>Rider: {d?.riderId ? <b className="text-kelate-sage">Assigned</b> : <b className="text-kelate-red">Unassigned</b>}</span>
            </div>
            {assignable && !d?.riderId && (
              <button
                data-testid={`order-assign-${o.orderNumber}`}
                onClick={() => setAssignTarget(o)}
                className="mt-3 flex items-center gap-1.5 rounded-sm bg-kelate-darkwood px-4 py-2 text-sm font-medium text-kelate-surface hover:bg-kelate-wood"
              >
                <Bike size={14} /> Appoint Rider
              </button>
            )}
            {assignable && d?.riderId && (
              <button
                data-testid={`order-reassign-${o.orderNumber}`}
                onClick={() => setAssignTarget(o)}
                className="mt-3 rounded-sm border border-kelate-line px-4 py-2 text-xs text-kelate-soft hover:border-kelate-wood"
              >
                Reassign rider
              </button>
            )}
          </div>
        );
      })}

      <Dialog open={!!assignTarget} onOpenChange={() => setAssignTarget(null)}>
        <DialogContent className="bg-kelate-surface border-kelate-line max-w-sm" data-testid="order-assign-modal">
          <DialogHeader>
            <DialogTitle className="text-kelate-ink">Appoint rider for #{assignTarget?.orderNumber}</DialogTitle>
            <DialogDescription className="text-kelate-soft">Choose a KELATE delivery staff member.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {riders.length === 0 && <p className="text-xs text-kelate-soft">No rider accounts yet. Create one under Staff.</p>}
            {riders.map((r) => (
              <button key={r.id} data-testid={`order-assign-rider-${r.id}`} onClick={() => assign(r)}
                className="flex w-full items-center justify-between rounded-sm border border-kelate-line px-3 py-2.5 text-sm hover:border-kelate-wood">
                <span className="font-medium text-kelate-ink">{r.name}</span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
